/**
 * NETGEAR AV-line (M4250 / M4350) + GS-series PoE catalog.
 *
 * All specs verified against netgear.com/business product pages on
 * 2026-05-15. URL cited per entry. Missing specs left undefined.
 *
 * Half-rack form factor (M4250-12* / M4250-10G2XF) is mapped to
 * `rack-1U` since each unit is 1U high and rack-mountable with
 * the supplied bracket.
 *
 * Non-PoE M4250 (MSM4214X) is excluded — only PoE switches belong
 * to the `poe-switch` category.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const NETGEAR_AV_LINE_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // M4250 AV-line — managed PoE+ / PoE++ for pro-AV
  // ─────────────────────────────────────────────────────────────

  {
    id: 'netgear-gsm4230p',
    manufacturer: 'NETGEAR',
    model: 'M4250-26G4F-PoE+ (GSM4230P)',
    category: 'poe-switch',
    tagline: 'AV-line managed PoE+ switch engineered for 1G AV over IP',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4230p/',
    specs: {
      totalPorts: 30,
      poePorts: 24,
      poeBudgetW: 300,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G RJ45 + 4× 1G SFP',
      switchingCapacityGbps: 60,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4230up',
    manufacturer: 'NETGEAR',
    model: 'M4250-26G4F-PoE++ (GSM4230UP)',
    category: 'poe-switch',
    tagline: 'AV-line managed PoE++ (90 W/port) for high-power AV over IP',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4230up/',
    specs: {
      totalPorts: 30,
      poePorts: 24,
      poeBudgetW: 1440,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '2× 1G RJ45 + 4× 1G SFP',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4230px',
    manufacturer: 'NETGEAR',
    model: 'M4250-26G4XF-PoE+ (GSM4230PX)',
    category: 'poe-switch',
    tagline: 'AV-line managed PoE+ switch with 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4230px/',
    specs: {
      totalPorts: 30,
      poePorts: 24,
      poeBudgetW: 480,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G RJ45 + 4× 10G SFP+',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4248p',
    manufacturer: 'NETGEAR',
    model: 'M4250-40G8F-PoE+ (GSM4248P)',
    category: 'poe-switch',
    tagline: 'AV-line 40-port managed PoE+ switch for pro-AV racks',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4248p/',
    specs: {
      totalPorts: 48,
      poePorts: 40,
      poeBudgetW: 480,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '8× 1G SFP',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4248ux',
    manufacturer: 'NETGEAR',
    model: 'M4250-40G8XF-PoE++ (GSM4248UX)',
    category: 'poe-switch',
    tagline: 'AV-line 40-port PoE++ (2880 W) with 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4248ux/',
    specs: {
      totalPorts: 48,
      poePorts: 40,
      poeBudgetW: 2880,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '8× 10G SFP+',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4212p',
    manufacturer: 'NETGEAR',
    model: 'M4250-10G2F-PoE+ (GSM4212P)',
    category: 'poe-switch',
    tagline: 'AV-line compact 8-port managed PoE+ for small AV installs',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4212p/',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 125,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G RJ45 + 2× 1G SFP',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4210px',
    manufacturer: 'NETGEAR',
    model: 'M4250-8G2XF-PoE+ (GSM4210PX)',
    category: 'poe-switch',
    tagline: 'AV-line compact fanless 8-port managed PoE+ with 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4210px/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 220,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 10G SFP+',
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'netgear-gsm4212px',
    manufacturer: 'NETGEAR',
    model: 'M4250-10G2XF-PoE+ (GSM4212PX)',
    category: 'poe-switch',
    tagline: 'AV-line 8-port managed PoE+ with 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4212px/',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 240,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G RJ45 + 2× 10G SFP+',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4212ux',
    manufacturer: 'NETGEAR',
    model: 'M4250-10G2XF-PoE++ (GSM4212UX)',
    category: 'poe-switch',
    tagline: 'AV-line 8-port managed PoE++ (90 W/port) with 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4212ux/',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 720,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '2× 1G RJ45 + 2× 10G SFP+',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // M4350 — L3 stackable
  // ─────────────────────────────────────────────────────────────

  {
    id: 'netgear-gsm4328',
    manufacturer: 'NETGEAR',
    model: 'M4350-24G4XF (GSM4328)',
    category: 'poe-switch',
    tagline: 'M4350 L3 stackable 24-port PoE+ with modular PSU and 10G uplinks',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4328/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 720,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gsm4352',
    manufacturer: 'NETGEAR',
    model: 'M4350-48G4XF (GSM4352)',
    category: 'poe-switch',
    tagline: 'M4350 L3 stackable 48-port PoE+ with virtual chassis stacking',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/fully-managed/gsm4352/',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 1440,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // GS-series unmanaged PoE+ (Essentials)
  // ─────────────────────────────────────────────────────────────

  // GS108PP (id: netgear-gs108pp) covered by networkSwitchCatalog.ts.

  {
    id: 'netgear-gs108lp',
    manufacturer: 'NETGEAR',
    model: 'GS108LP',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ unmanaged with FlexPoE (60 W)',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/unmanaged/gs108lp/',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 60,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: false,
    },
  },

  // GS308PP (id: netgear-gs308pp) covered by networkSwitchCatalog.ts.

  {
    id: 'netgear-gs316pp',
    manufacturer: 'NETGEAR',
    model: 'GS316PP',
    category: 'poe-switch',
    tagline: '16-port Gigabit unmanaged PoE+ Essentials switch (183 W), fanless',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/unmanaged/gs316pp/',
    specs: {
      totalPorts: 16,
      poePorts: 16,
      poeBudgetW: 183,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'netgear-gs324p',
    manufacturer: 'NETGEAR',
    model: 'GS324P',
    category: 'poe-switch',
    tagline: '24-port Gigabit unmanaged PoE+ Essentials switch (190 W), fanless',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/unmanaged/gs324p/',
    specs: {
      totalPorts: 24,
      poePorts: 16,
      poeBudgetW: 190,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // GS-series smart / easy-smart PoE
  // ─────────────────────────────────────────────────────────────

  {
    id: 'netgear-gs308ep',
    manufacturer: 'NETGEAR',
    model: 'GS308EP',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ Easy Smart Essentials switch (62 W), fanless',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/plus/gs308ep/',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 62,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'netgear-gs308epp',
    manufacturer: 'NETGEAR',
    model: 'GS308EPP',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ Easy Smart Essentials switch (123 W), fanless',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/plus/gs308epp/',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 123,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'netgear-gs316ep',
    manufacturer: 'NETGEAR',
    model: 'GS316EP',
    category: 'poe-switch',
    tagline: '16-port Gigabit PoE+ Easy Smart switch (180 W) with 1 SFP, fanless',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/easy-smart/gs316ep/',
    specs: {
      totalPorts: 16,
      poePorts: 15,
      poeBudgetW: 180,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× SFP 1G',
      switchingCapacityGbps: 32,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'netgear-gs316epp',
    manufacturer: 'NETGEAR',
    model: 'GS316EPP',
    category: 'poe-switch',
    tagline: '16-port high-power PoE+ Easy Smart switch (231 W) with 1 SFP, fanless',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/easy-smart/gs316epp/',
    specs: {
      totalPorts: 16,
      poePorts: 15,
      poeBudgetW: 231,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× SFP 1G',
      switchingCapacityGbps: 32,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'netgear-gs324tp',
    manufacturer: 'NETGEAR',
    model: 'GS324TP (S350)',
    category: 'poe-switch',
    tagline: 'S350 24-port Gigabit PoE+ smart managed switch (190 W) with 2 SFP',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/smart/gs324tp/',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 190,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP 1G',
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'netgear-gs510tpp',
    manufacturer: 'NETGEAR',
    model: 'GS510TPP',
    category: 'poe-switch',
    tagline: '8-port Gigabit high-power PoE+ smart switch (190 W) with 2 SFP',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/smart-cloud/gs510tpp/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 190,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP 1G',
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: false,
    },
  },

  {
    id: 'netgear-gs728tppv3',
    manufacturer: 'NETGEAR',
    model: 'GS728TPPv3',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ smart switch (380 W) with 4 SFP, cloud-managed',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/smart-cloud/gs728tppv3/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 380,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP 1G',
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },
];
